import { useMemo } from "react";
import type { BillOut } from "../../../../../api/bills";
import { useAppSelector } from "../../../../../store/hooks";
import type { ClosingBill } from "../../../../../store";

/**
 * Закриванията на този терминал, които още чакат принтера.
 *
 * `closingBillIds` — ВСИЧКИ: заключването (нито поръчка, нито второ закриване)
 * важи за всеки служител на терминала, иначе на общ терминал следващият би
 * пуснал второ закриване на същата сметка. `ownClosingBills` — само на текущия
 * служител: само за тях снимката се връща в списъка, когато `GET /bills` вече
 * не я дава — чужда маса, която сървърът не показва на този служител, не се
 * появява изневиделица.
 */
export function useClosingBills(): {
  ownClosingBills: ClosingBill[];
  closingBillIds: ReadonlySet<number>;
} {
  const allClosingBills = useAppSelector((state) => state.bills.closingBills);
  const currentUserId = useAppSelector((state) => state.auth.userId);
  return useMemo(
    () => ({
      ownClosingBills: allClosingBills.filter(
        (closing) => closing.userId === currentUserId,
      ),
      closingBillIds: new Set(allClosingBills.map((closing) => closing.billId)),
    }),
    [allClosingBills, currentUserId],
  );
}

/**
 * Списъкът с маси, докато тече закриване: кутийката стои на мястото си и във
 * вида отпреди натискането, каквото и да връща `GET /bills` — при режим
 * „никога" сметката вече я няма в отговора, при „винаги" е закрита без бон и
 * би слязла най-долу. Входът не се променя.
 */
export function mergeClosingBills(
  bills: BillOut[],
  closingBills: ClosingBill[],
): BillOut[] {
  const snapshotsById = new Map<number, BillOut>();
  for (const closing of closingBills) {
    if (closing.snapshot) snapshotsById.set(closing.billId, closing.snapshot);
  }
  if (snapshotsById.size === 0) return bills;
  const merged = bills.map((bill) => snapshotsById.get(bill.id) ?? bill);
  for (const [billId, snapshot] of snapshotsById) {
    if (!bills.some((bill) => bill.id === billId)) merged.push(snapshot);
  }
  return merged;
}
